import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { userApi } from '../lib/userApi'
import type { WaybookLoginResponse, WaybookUser } from '../types'

interface RegisterPayload {
  email: string
  password: string
  display_name: string
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  description?: string | null
}

interface UserAuthState {
  user: WaybookUser | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isHydrated: boolean
  loading: boolean
  error: string | null
  isPinVerified: boolean
  setHydrated: () => void
  setTokens: (accessToken: string, refreshToken: string) => void
  login: (email: string, password: string) => Promise<void>
  register: (payload: RegisterPayload) => Promise<void>
  fetchProfile: () => Promise<void>
  logout: () => void
  handleUnauthorized: () => void
  applyAuthResponse: (authResponse: WaybookLoginResponse) => void
  verifyPin: (pin: string) => Promise<void>
}

const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isHydrated: false,
  loading: false,
  error: null,
  isPinVerified: false,
}

const persistOptions = {
  name: 'waybook-user-auth',
  partialize: (state: UserAuthState) => ({
    user: state.user,
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    isAuthenticated: state.isAuthenticated,
    isPinVerified: state.isPinVerified,
  }),
  onRehydrateStorage: () => (state?: UserAuthState) => {
    state?.setHydrated()
  },
}

export const useUserAuthStore = create<UserAuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setHydrated: () => set({ isHydrated: true }),

      setTokens: (accessToken: string, refreshToken: string) => {
        set({
          accessToken,
          refreshToken,
          isAuthenticated: !!accessToken,
          isHydrated: true,
          isPinVerified: false,
        })
      },

      login: async (email: string, password: string) => {
        set({ loading: true, error: null })
        try {
          const response = await userApi.post<{ data: WaybookLoginResponse }>('/auth/login', {
            email,
            password,
          })

          const data = response.data?.data
          if (!data?.user || !data?.tokens) {
            throw new Error('Invalid login response')
          }

          get().applyAuthResponse(data)
        } catch (error: any) {
          const message =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            'Failed to log in'
          set({ error: message })
          throw error
        } finally {
          set({ loading: false })
        }
      },

      register: async (payload: RegisterPayload) => {
        set({ loading: true, error: null })
        try {
          const response = await userApi.post<{ data: WaybookLoginResponse }>('/auth/register', payload)
          const data = response.data?.data
          if (!data?.user || !data?.tokens) {
            throw new Error('Invalid registration response')
          }
          get().applyAuthResponse(data)
        } catch (error: any) {
          const message =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            'Failed to register'
          set({ error: message })
          throw error
        } finally {
          set({ loading: false })
        }
      },

      fetchProfile: async () => {
        try {
          const response = await userApi.get<{ data: WaybookUser }>('/auth/me')
          const user = response.data?.data
          if (!user) {
            throw new Error('Invalid profile response')
          }

          set({ user })
        } catch (error: any) {
          if (error?.response?.status === 401) {
            get().handleUnauthorized()
            return
          }
          const message =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            'Failed to load profile'
          set({ error: message })
          throw error
        }
      },

      logout: () => {
        set({ ...initialState, isHydrated: true })
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('waybook-user-auth')
        }
      },

      handleUnauthorized: () => {
        set({ ...initialState, isHydrated: true })
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('waybook-user-auth')
        }
      },

      applyAuthResponse: (authResponse: WaybookLoginResponse) => {
        const { user, tokens } = authResponse
        get().setTokens(tokens.access_token, tokens.refresh_token)
        set({
          user,
          isAuthenticated: true,
          isHydrated: true,
          isPinVerified: false,
        })
      },

      verifyPin: async (pin: string) => {
        if (pin === '7887') {
          set({ isPinVerified: true })
          return
        }

        get().logout()
        throw new Error('Invalid PIN code. You have been logged out.')
      },
    }),
    persistOptions
  )
) 
